import type {
  IIncidentProvider,
  Incident,
  CreateIncidentParams,
  IncidentStatus,
  IncidentSeverity,
} from './types';

const now = () => new Date().toISOString();
const mins = (n: number) => new Date(Date.now() - n * 60_000).toISOString();

let counter = 5;

const seed: Incident[] = [
  {
    id: 'INC-001',
    title: 'Sensor de temperatura MDC-01 fuera de rango',
    description: 'Lectura sostenida >85°C durante 4 minutos en máquina MDC-01.',
    severity: 'critical',
    status: 'triggered',
    serviceKey: 'expai-plant',
    affectedService: 'Línea de mecanizado MDC-01',
    runbookUrl: 'https://runbooks.expai.local/plant/temp-overheat',
    createdAt: mins(8),
    updatedAt: mins(8),
  },
  {
    id: 'INC-002',
    title: 'Latencia de inferencia p95 > 800ms',
    description: 'Gateway YOLO11 supera SLO de 500ms en p95.',
    severity: 'high',
    status: 'acknowledged',
    serviceKey: 'expai-ml',
    affectedService: 'ML Inference Gateway',
    runbookUrl: 'https://runbooks.expai.local/ml/latency-slo',
    createdAt: mins(35),
    updatedAt: mins(20),
    assignee: 'ops@expai',
  },
  {
    id: 'INC-003',
    title: 'Error rate > 5% en gateway ML',
    description: 'HTTP 5xx en /v1/predict supera el umbral configurado.',
    severity: 'high',
    status: 'triggered',
    serviceKey: 'expai-ml',
    affectedService: 'ML Inference Gateway',
    runbookUrl: 'https://runbooks.expai.local/ml/5xx-spike',
    createdAt: mins(12),
    updatedAt: mins(12),
  },
  {
    id: 'INC-004',
    title: 'Fallo de sincronización ERP n8n',
    description: 'Workflow Odoo→MES devolvió 401 en último ciclo.',
    severity: 'warning',
    status: 'resolved',
    serviceKey: 'expai-erp',
    affectedService: 'ERP Sync (Odoo ↔ MES)',
    createdAt: mins(220),
    updatedAt: mins(150),
    resolvedAt: mins(150),
  },
  {
    id: 'INC-005',
    title: 'Disco >80% en Fly.io MLflow',
    description: 'Volumen mlflow-data al 82%. Requiere limpieza de artefactos.',
    severity: 'info',
    status: 'resolved',
    serviceKey: 'expai-infra',
    affectedService: 'MLflow Tracking Server',
    runbookUrl: 'https://runbooks.expai.local/infra/disk-cleanup',
    createdAt: mins(720),
    updatedAt: mins(600),
    resolvedAt: mins(600),
  },
];

export class MockProvider implements IIncidentProvider {
  private store: Incident[] = [...seed];

  async createIncident(params: CreateIncidentParams): Promise<Incident> {
    counter += 1;
    const id = `INC-${String(counter).padStart(3, '0')}`;
    const incident: Incident = {
      id,
      title: params.title,
      description: params.description,
      severity: params.severity,
      status: 'triggered',
      serviceKey: params.serviceKey ?? 'expai-mock',
      createdAt: now(),
      updatedAt: now(),
    };
    this.store.unshift(incident);
    return incident;
  }

  private update(id: string, patch: Partial<Incident>): Incident | null {
    const idx = this.store.findIndex((i) => i.id === id);
    if (idx < 0) return null;
    const updated: Incident = { ...this.store[idx], ...patch, updatedAt: now() };
    this.store[idx] = updated;
    return updated;
  }

  async acknowledgeIncident(id: string): Promise<Incident> {
    const updated = this.update(id, { status: 'acknowledged' });
    if (!updated) throw new Error(`Incident ${id} not found`);
    return updated;
  }

  async resolveIncident(id: string): Promise<Incident> {
    const ts = now();
    const updated = this.update(id, { status: 'resolved', resolvedAt: ts });
    if (!updated) throw new Error(`Incident ${id} not found`);
    return updated;
  }

  async listIncidents(filter?: { status?: IncidentStatus; limit?: number }): Promise<Incident[]> {
    let out = [...this.store];
    if (filter?.status) out = out.filter((i) => i.status === filter.status);
    if (filter?.limit) out = out.slice(0, filter.limit);
    return out;
  }

  async getIncident(id: string): Promise<Incident | null> {
    return this.store.find((i) => i.id === id) ?? null;
  }
}

export type { IncidentSeverity };
